// ** React Imports
import { useEffect, useState, useRef, useContext } from "react";
import { Prompt } from "react-router-dom";

// ** Custom Components
import Block from "components/layout/Block";
import WatcherCameraCloudflare from "./components/WatcherCameraCloudflare";

// ** Custom Hooks
import useWHIPClient from "./cloudflare/useWHIPClient";
import useWHEPClient from "./cloudflare/useWHEPClient";

// ** Utils & Helperss
import api from "helpers/ApiClient";
import { dateFormat } from "helpers/dateFormat";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const VideoChatCloudflare = ({
	changeMainWrapperPositionInPhone,
	sendWebsocketMessage,
	videoChat: {
		chatPartnerStreamStatus,
		chatPartnerCameraAndMic = null,
		watcherHash,
		broadcastId,
	},
	setVideoChat,
	setConversation,
	loggedUser,
	publishUrl,
	conversation,
	setLiveInput,
	closeVideoChat,
	sessionId,
}) => {
	const { isPhone } = useContext(deviceTypeContext);

	const [showYou, setShowYou] = useState(null);
	const [selectedCamera, setSelectedCamera] = useState("user");
	const [camOff, setCamOff] = useState(false);

	const [isPublisherConnected, setIsPublisherConnected] = useState(false);

	const publisherCameraRef = useRef(null);
	const showingInProgress = useRef(false);
	const reactPlayerRef = useRef(null);
	const videoChatRef = useRef(null);

	const {
		disconnectPublisher,
		muteStream,
		unMuteStream,
		videoOff,
		videoOn,
		setLocalStream,
		localStream,
	} = useWHIPClient({
		endpoint: publishUrl,
		setIsPublisherConnected,
	});

	const { disconnectWatcher } = useWHEPClient(
		watcherHash,
		reactPlayerRef.current
	);

	const handleFinishTransmiting = () => {
		if (broadcastId) {
			api.delete(
				`camera/conversation?id=${broadcastId}`,
				{},
				{
					headers: {
						Accept: "application/api.1.1+json",
					},
				}
			);
		}
		setIsPublisherConnected(false);

		setLiveInput({
			publishUrl: null,
			playbackUrl: null,
		});

		disconnectPublisher();
		disconnectWatcher();

		sendWebsocketMessage({
			action: "stream_deleted",
			sessionId,
		});
		setVideoChat((prev) => {
			return {
				...prev,
				chatPartnerStreamStatus: "initial",
				publisherHash: null,
				publisherPicture: null,
				broadcastId: null,
			};
		});

		if (typeof changeMainWrapperPositionInPhone === "function") {
			changeMainWrapperPositionInPhone("fixed");
		}

		closeVideoChat();
	};

	useEffect(() => {
		const handleBeforeUnload = () => {
			handleFinishTransmiting();
			return;
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
			handleFinishTransmiting();
		};
	}, []);

	useEffect(() => {
		if (chatPartnerStreamStatus == "stream_off") {
			disconnectWatcher();
			sendWebsocketMessage({
				action: "stream_request",
				profileStatus: loggedUser.profileStatus,
				onlineStatus: loggedUser.online,
				sessionId,
			});
		}
	}, [chatPartnerStreamStatus]);

	useEffect(() => {
		if (!isPublisherConnected && showYou && localStream) {
			api.post(
				`camera/conversation?userId=${conversation?.user?.id}`,
				{},
				{
					headers: {
						Accept: "application/api.1.1+json",
					},
				}
			)
				.then(({ data } = {}) => {
					if (!data) {
						return;
					}

					const currentDate = dateFormat(new Date());
					const ss = publisherCameraRef.current?.getScreenshot();

					setLiveInput({
						publishUrl: data?.cf_info?.webRTC || null,
						playbackUrl: data?.cf_info?.webRTCPlayback || null,
					});
					setVideoChat((prev) => {
						return {
							...prev,
							publisherHash: data?.cf_info?.webRTCPlayback,
							broadcastId: data?.conversation?.broadcastId,
							publisherPicture: ss ? ss : null,
						};
					});
					setConversation((prev) => {
						return {
							...prev,
							messages: [
								...prev.messages,
								{
									id: currentDate,
									message: [""],
									additional_status: 1,
									send: true,
									read: false,
									file_big: null,
									file_hash: null,
									file_id: null,
									file_small: null,
									date: currentDate,
								},
							],
						};
					});
					sendWebsocketMessage({
						action: "stream_created",
						publisherHash: data?.cf_info?.webRTCPlayback,
						messageDate: currentDate,
						picture: ss ? ss : null,
						sessionId,
					});
				})
				.catch((error) => console.error("Error:", error))
				.finally(() => {
					showingInProgress.current = false;
				});
		}
	}, [showYou, isPublisherConnected, localStream]);

	const handleClickShowYou = () => {
		if (!camOff && isPublisherConnected) {
			setCamOff(true);
			videoOff();
		} else if (camOff && isPublisherConnected) {
			setCamOff(false);
			videoOn();
		}

		if (!showYou && !isPublisherConnected && !showingInProgress.current) {
			setShowYou(true);
			showingInProgress.current = true;
		}
	};

	return (
		<>
			<Prompt
				message={() => {
					handleFinishTransmiting();
					// Prompt expects a function that returns a string or boolean as a message.
					return true;
				}}
			/>

			<Block
				relative
				width={isPhone ? "100%" : 400}
				style={{
					backgroundColor: "#000",
					position: "relative",
					margin: "0 auto 1rem auto",
				}}
				ref={videoChatRef}
			>
				<WatcherCameraCloudflare
					watcherMediaStream={reactPlayerRef?.current?.srcObject}
					watcherHash={watcherHash}
					isPublisherConnected={isPublisherConnected}
					publisherMuteStream={() => {
						muteStream();
					}}
					publisherUnmuteStream={() => {
						unMuteStream();
					}}
					publisherCameraRef={publisherCameraRef}
					chatPartnerCameraAndMic={chatPartnerCameraAndMic}
					chatPartnerStreamStatus={chatPartnerStreamStatus}
					showYou={showYou}
					camOff={camOff}
					setCamOff={setCamOff}
					handleClickShowYou={handleClickShowYou}
					reactPlayerRef={reactPlayerRef}
					selectedCamera={selectedCamera}
					setSelectedCamera={setSelectedCamera}
					showingInProgress={showingInProgress}
					videoChatRef={videoChatRef}
					loggedUser={loggedUser}
					setLocalStream={setLocalStream}
					setShowYou={setShowYou}
				/>
			</Block>
		</>
	);
};

export default VideoChatCloudflare;
